import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Mail, Newspaper, Send, Workflow, Database, Target, BarChart3, Inbox,
  Users, Layers, Activity, ShieldCheck, Sparkles, GitBranch, ListChecks,
  Zap, Eye, TrendingUp, Code2, Repeat,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "email-campaigns": {
    eyebrow: "Email & Automation · Email",
    title: "Email Campaigns",
    sub: "Broadcast emails — launches, promos and announcements — that drive revenue without burning your list.",
    metaDesc: "Email campaign management: planning, design, copy, segmentation, A/B testing and reporting.",
    intro:
      "We plan, design, copy and ship broadcast email campaigns — product launches, seasonal pushes, announcements — segmented and tested so every send moves the number without burning the list.",
    features: [
      { icon: Mail, title: "Campaign planning", description: "Quarterly calendar tied to launches and seasons." },
      { icon: Sparkles, title: "Design + copy", description: "On-brand templates and direct-response copywriting." },
      { icon: Users, title: "Segmentation", description: "Behavior, RFM and engagement-based sends." },
      { icon: Activity, title: "A/B testing", description: "Subject, hero, offer and CTA tests every send." },
      { icon: BarChart3, title: "Reporting", description: "Per-campaign revenue, LTV impact and segment lift." },
      { icon: Inbox, title: "Deliverability hygiene", description: "Frequency capping and engagement-based suppression." },
    ],
    process: [
      { step: "01", title: "Plan", description: "Calendar, offers, audiences and KPIs." },
      { step: "02", title: "Build", description: "Design, copy, segments and tests." },
      { step: "03", title: "Send", description: "Phased sends with deliverability monitoring." },
      { step: "04", title: "Report", description: "Per-campaign performance and learnings." },
    ],
    deliverables: ["Quarterly campaign calendar", "Email designs + copy", "Segment definitions", "A/B test results", "Per-campaign reports", "Quarterly review"],
    techStack: ["Klaviyo", "HubSpot", "Customer.io", "Iterable", "Mailchimp"],
    pricing: [
      { name: "Starter", price: "$2k", period: "month", features: ["4 campaigns / month", "1 segment / send", "Basic reporting"] },
      { name: "Growth", price: "$4.5k", period: "month", highlighted: true, features: ["8 campaigns / month", "Advanced segments", "A/B tests"] },
      { name: "Scale", price: "$9k", period: "month", features: ["12+ campaigns", "Predictive segments", "Custom dashboards"] },
    ],
    faqs: [
      { q: "Will you write copy?", a: "Yes — direct-response copywriters write every campaign in your brand voice." },
      { q: "What about discounts?", a: "We use offer strategy carefully — discounts have a place but won't be the default lever." },
    ],
  },
  "newsletter-strategy": {
    eyebrow: "Email & Automation · Email",
    title: "Newsletter Strategy",
    sub: "An owned-audience program that compounds — and a newsletter people actually open.",
    metaDesc: "Newsletter strategy: positioning, format, cadence, growth, monetization and editorial workflow.",
    intro:
      "We design newsletter programs that grow owned audience and influence — positioning, format, cadence, editorial workflow, growth loops and (optionally) monetization. From founder-led B2B to consumer media plays.",
    features: [
      { icon: Newspaper, title: "Positioning + format", description: "Who it's for, what it covers, why people open it." },
      { icon: Repeat, title: "Cadence + workflow", description: "Sustainable production cadence and editorial pipeline." },
      { icon: TrendingUp, title: "Growth loops", description: "Referral, co-marketing, paid acquisition and SEO loops." },
      { icon: Users, title: "Audience research", description: "Subscriber interviews and engagement segments." },
      { icon: Sparkles, title: "Editorial design", description: "Distinct, recognizable visual identity per issue." },
      { icon: BarChart3, title: "KPIs + reporting", description: "Open, click, growth, churn and revenue tracking." },
    ],
    process: [
      { step: "01", title: "Strategy", description: "Positioning, format and audience definition." },
      { step: "02", title: "System", description: "Editorial workflow and design templates." },
      { step: "03", title: "Launch", description: "First issues, growth loops live." },
      { step: "04", title: "Optimize", description: "Test format, growth tactics and monetization." },
    ],
    deliverables: ["Positioning + format doc", "Editorial workflow", "Design templates", "Growth plan", "Launch issues", "Monthly performance review"],
    techStack: ["Beehiiv", "Substack", "Ghost", "ConvertKit", "Mailchimp", "Letterhead"],
    pricing: [
      { name: "Strategy", price: "$5k", features: ["Positioning + format", "Editorial workflow", "Growth plan"] },
      { name: "Strategy + launch", price: "$12k", period: "project", highlighted: true, features: ["Strategy", "Design templates", "First 4 issues"] },
      { name: "Ongoing", price: "$6k", period: "month", features: ["Editorial + design", "Growth experiments", "Monthly review"] },
    ],
    faqs: [
      { q: "Can you write the newsletter?", a: "Yes — ongoing plans include editorial and design execution." },
      { q: "Sponsorships?", a: "Optional — we set up the program but you own publisher/advertiser relationships." },
    ],
  },
  "transactional-email": {
    eyebrow: "Email & Automation · Email",
    title: "Transactional Email",
    sub: "Reliable, deliverable, on-brand transactional emails — from confirmations to receipts.",
    metaDesc: "Transactional email setup: provider integration, templates, deliverability, monitoring and brand-aligned design.",
    intro:
      "We design and ship transactional email infrastructure — receipts, confirmations, password resets, magic links, notifications — with the deliverability, templating and monitoring of a serious product team.",
    features: [
      { icon: Send, title: "Provider integration", description: "Postmark, Resend, SendGrid, SES or your provider." },
      { icon: Sparkles, title: "Brand templates", description: "Responsive React Email / MJML templates on-brand." },
      { icon: ShieldCheck, title: "Deliverability", description: "DMARC, SPF, DKIM and BIMI setup and monitoring." },
      { icon: Code2, title: "Backend integration", description: "Server functions, webhooks and idempotency keys." },
      { icon: Inbox, title: "Suppression handling", description: "Bounces and complaints handled cleanly." },
      { icon: Eye, title: "Monitoring + logs", description: "Per-message logs, retries and alerting on failures." },
    ],
    process: [
      { step: "01", title: "Audit", description: "Inventory transactional emails and current setup." },
      { step: "02", title: "Templates", description: "Design and build all required templates." },
      { step: "03", title: "Integrate", description: "Backend wiring, webhooks, idempotency." },
      { step: "04", title: "Verify", description: "End-to-end QA and deliverability checks." },
    ],
    deliverables: ["Email inventory", "Branded templates", "Provider + DNS setup", "Backend integration", "Monitoring + alerting", "Documentation"],
    techStack: ["Postmark", "Resend", "SendGrid", "SES", "Mailgun", "React Email", "MJML"],
    pricing: [
      { name: "Setup", price: "$5k", features: ["5 templates", "Provider + DNS", "Basic monitoring"] },
      { name: "Full system", price: "$14k", period: "project", highlighted: true, features: ["12+ templates", "Idempotency + webhooks", "Suppression handling"] },
      { name: "Managed", price: "$2.5k", period: "month", features: ["Ongoing template work", "Deliverability monitoring", "On-call for incidents"] },
    ],
    faqs: [
      { q: "Which provider should we use?", a: "Postmark for top deliverability, Resend for DX, SES for cost at scale — we'll recommend per your stack and volume." },
      { q: "Do you handle inbox testing?", a: "Yes — multi-client preview and inbox placement testing across Gmail, Outlook, Apple Mail." },
    ],
  },
  "marketing-automation": {
    eyebrow: "Email & Automation · Automation",
    title: "Marketing Automation",
    sub: "Lifecycle flows that nurture, convert and retain — without spamming.",
    metaDesc: "Marketing automation flows: welcome, abandoned cart, post-purchase, win-back and SMS/push lifecycle.",
    intro:
      "We design and ship lifecycle flows that meet customers at every moment — welcome, abandoned cart, post-purchase, win-back, VIP — instrumented with revenue attribution and continuous A/B testing.",
    features: [
      { icon: Workflow, title: "Lifecycle flows", description: "Welcome, cart, post-purchase, win-back and more." },
      { icon: GitBranch, title: "Branching logic", description: "Behavior, RFM and predictive branching." },
      { icon: Zap, title: "Multi-channel", description: "Email + SMS + push as one orchestrated lifecycle." },
      { icon: Activity, title: "A/B testing", description: "Subject, copy and offer tests inside flows." },
      { icon: BarChart3, title: "Revenue attribution", description: "Per-flow and per-step revenue + LTV impact." },
      { icon: ListChecks, title: "Suppression + frequency", description: "Smart caps so high-value customers don't fatigue." },
    ],
    process: [
      { step: "01", title: "Audit", description: "Current flows, list health and performance." },
      { step: "02", title: "Architect", description: "Map every lifecycle moment to a flow." },
      { step: "03", title: "Build", description: "Design, copy, segments, tracking, tests." },
      { step: "04", title: "Optimize", description: "Continuous testing and refresh." },
    ],
    deliverables: ["Lifecycle map", "8–15 production flows", "Segment library", "A/B test plan", "Revenue dashboard", "Monthly review"],
    techStack: ["Klaviyo", "Customer.io", "HubSpot", "Iterable", "Braze", "Attentive"],
    pricing: [
      { name: "Core flows", price: "$3k", period: "month", features: ["5 core flows", "Email only", "Monthly reporting"] },
      { name: "Full lifecycle", price: "$6k", period: "month", highlighted: true, features: ["12+ flows", "Email + SMS", "A/B testing"] },
      { name: "Enterprise", price: "$12k", period: "month", features: ["Multi-channel", "Predictive segments", "Dedicated strategist"] },
    ],
    faqs: [
      { q: "Will you migrate from another ESP?", a: "Yes — list, template and flow migration with QA and minimal downtime." },
      { q: "Do you do SMS too?", a: "Yes — orchestrated with email through Attentive, Postscript or Klaviyo SMS." },
    ],
  },
  "crm-integration": {
    eyebrow: "Email & Automation · Automation",
    title: "CRM Integration",
    sub: "Connect your CRM to product, data warehouse and marketing tools — cleanly.",
    metaDesc: "CRM integration services for HubSpot, Salesforce and Pipedrive: data sync, deduplication, lifecycle mapping and reporting.",
    intro:
      "We integrate HubSpot, Salesforce, Pipedrive or Attio with your product, data warehouse and marketing tools — clean data flow, deduped records, mapped lifecycle stages and reports leadership actually trusts.",
    features: [
      { icon: Database, title: "Data sync", description: "Two-way sync between CRM, product DB and warehouse." },
      { icon: Layers, title: "Object modeling", description: "Contacts, accounts, deals and custom objects done right." },
      { icon: ShieldCheck, title: "Dedup + hygiene", description: "Match keys, dedupe rules and merge workflows." },
      { icon: GitBranch, title: "Lifecycle stages", description: "MQL → SQL → Opp → Customer mapping you can defend." },
      { icon: Activity, title: "Event tracking", description: "Product events into CRM via Segment, Rudderstack or HighTouch." },
      { icon: BarChart3, title: "Reporting + dashboards", description: "Funnel, attribution and pipeline dashboards." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Stack, objects and reporting needs." },
      { step: "02", title: "Model", description: "Schema, lifecycle stages and ownership." },
      { step: "03", title: "Integrate", description: "Build syncs, automations and dedup." },
      { step: "04", title: "Enable", description: "Train teams and ship dashboards." },
    ],
    deliverables: ["CRM model + schema doc", "Sync + dedup rules", "Lifecycle automations", "Reverse-ETL pipelines", "Dashboards", "Team enablement"],
    techStack: ["HubSpot", "Salesforce", "Pipedrive", "Attio", "Segment", "Hightouch", "Census", "Snowflake", "BigQuery"],
    pricing: [
      { name: "Setup", price: "$8k", features: ["1 CRM", "Core syncs", "Lifecycle mapping"] },
      { name: "Full integration", price: "$22k", period: "project", highlighted: true, features: ["CRM + warehouse", "Reverse ETL", "Dashboards"] },
      { name: "Managed", price: "$5k", period: "month", features: ["Ongoing improvements", "RevOps support", "Monitoring"] },
    ],
    faqs: [
      { q: "Which CRM should we pick?", a: "HubSpot for marketing-led SMB/MM, Salesforce for sales-led enterprise, Attio for product-led modern teams." },
      { q: "Do you do reverse ETL?", a: "Yes — Hightouch, Census or custom pipelines from warehouse → CRM and tools." },
    ],
  },
  "lead-scoring": {
    eyebrow: "Email & Automation · Automation",
    title: "Lead Scoring",
    sub: "Score leads on fit AND behavior so sales chases the right deals.",
    metaDesc: "Lead scoring models: fit + behavior + intent scoring, predictive ML models and CRM integration.",
    intro:
      "We design lead scoring models that combine ICP fit, behavioral signals and intent data — calibrated against closed-won history and synced into your CRM so SDRs work the highest-value pipeline first.",
    features: [
      { icon: Target, title: "ICP + fit scoring", description: "Firmographic and demographic fit modeling." },
      { icon: Activity, title: "Behavior scoring", description: "Product, content and email engagement signals." },
      { icon: Sparkles, title: "Intent integration", description: "6sense, Bombora and G2 intent layered in." },
      { icon: TrendingUp, title: "Predictive ML", description: "Optional ML model trained on closed-won/lost." },
      { icon: GitBranch, title: "Routing + alerts", description: "Score-based routing, alerts and SDR queues." },
      { icon: BarChart3, title: "Calibration + reporting", description: "Monthly calibration vs conversion rates." },
    ],
    process: [
      { step: "01", title: "Define ICP", description: "Workshops and closed-won analysis." },
      { step: "02", title: "Design model", description: "Fit + behavior + intent scoring rules." },
      { step: "03", title: "Implement", description: "Build in CRM, wire alerts and routing." },
      { step: "04", title: "Calibrate", description: "Monthly review vs actual outcomes." },
    ],
    deliverables: ["ICP definition", "Scoring model doc", "CRM implementation", "Routing + alerts", "Calibration dashboard", "Monthly review"],
    techStack: ["HubSpot", "Salesforce", "MadKudu", "6sense", "Clearbit", "Bombora", "G2"],
    pricing: [
      { name: "Model design", price: "$5k", features: ["ICP workshop", "Scoring model", "Implementation guide"] },
      { name: "Build + ship", price: "$15k", period: "project", highlighted: true, features: ["Model design", "CRM implementation", "Routing + alerts"] },
      { name: "Predictive", price: "$30k", period: "project", features: ["ML model", "Warehouse training data", "Continuous calibration"] },
    ],
    faqs: [
      { q: "Do we need predictive ML?", a: "Only above ~500 closed deals/year. Below that, rules-based scoring outperforms." },
      { q: "Will sales actually use it?", a: "Yes — we ship enablement and calibrate monthly so SDRs trust the score." },
    ],
  },
};

export const Route = createFileRoute("/marketing/email-automation/$child")({
  component: EmailAutomationChild,
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item };
  },
  head: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) return {};
    return {
      meta: [
        { title: `${item.title} — CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: `/marketing/email-automation/${params.child}` },
      ],
      links: [{ rel: "canonical", href: `https://coderlity.com/marketing/email-automation/${params.child}` }],
    };
  },
});

function EmailAutomationChild() {
  const { item } = Route.useLoaderData();
  return <ServicePage data={item} />;
}
